import { useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCart } from "../../context/CartContext";
import olifubraudImg from "../../resources/braud/olifubraud.JPG";
import snittubraudImg from "../../resources/braud/snittubraud.JPG";
import donutsImg from "../../resources/minidonuts.JPG";

const formatISK = (n) =>
  new Intl.NumberFormat("is-IS", { maximumFractionDigits: 0 }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

const BREAD_PRICES = { "Ólífubrauð": 1290, "Snittubrauð": 990 };
const MINIDONUT_PRICE_EACH = 275;

const INPUT = "w-full h-12 px-4 bg-white border border-amber-200 rounded-xl text-stone-800 font-serif text-sm focus:outline-none focus:ring-2 focus:ring-[#C29979]/30";
const LABEL = "block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5";

export const Pantanir = () => {
  const { addItems, items: cartItems } = useCart();
  const cartCount = cartItems.length;

  const [lines, setLines] = useState([]);
  const [addedToCart, setAddedToCart] = useState(false);
  const [error, setError] = useState("");
  const [breadDraft, setBreadDraft] = useState({ bread: "", quantity: "" });
  const [miniQty, setMiniQty] = useState("");

  const totals = useMemo(() => {
    const totalISK = lines.reduce((sum, l) => {
      const p = l.details?.price;
      const q = Number(l.details?.quantity ?? 1);
      if (typeof p !== "number" || !Number.isFinite(q) || q <= 0) return sum;
      return sum + p * q;
    }, 0);
    return { count: lines.length, totalISK };
  }, [lines]);

  const addBreadLine = () => {
    setError("");
    if (!breadDraft.bread) return setError("Vinsamlegast veldu brauðtegund.");
    if (!breadDraft.quantity) return setError("Vinsamlegast veldu magn.");
    setLines(prev => [...prev, {
      id: uuidv4(), type: "bread",
      details: { ...breadDraft, quantity: Number(breadDraft.quantity), price: BREAD_PRICES[breadDraft.bread] ?? 0 }
    }]);
    setBreadDraft({ bread: "", quantity: "" });
  };

  const addMiniLine = () => {
    setError("");
    if (!miniQty) return setError("Vinsamlegast veldu magn af minidonuts.");
    if (Number(miniQty) < 30) return setError("Lágmarksfjöldi minidonuts er 30 stk.");
    setLines(prev => [...prev, {
      id: uuidv4(), type: "minidonut",
      details: { quantity: Number(miniQty), price: MINIDONUT_PRICE_EACH }
    }]);
    setMiniQty("");
  };

  const removeLine = (id) => setLines(prev => prev.filter(l => l.id !== id));

  const handleAddToCart = () => {
    if (lines.length === 0) { setError("Vinsamlegast bættu við a.m.k. einni vöru."); return; }
    setError("");
    const newCartItems = lines.map(l => {
      const label = l.type === "bread"
        ? `${l.details.bread} (${l.details.quantity} stk.)`
        : `Mini-donuts (${l.details.quantity} stk.)`;
      const price = (l.details.price ?? 0) * Number(l.details.quantity);
      return { cartId: uuidv4(), type: l.type, label, price, details: l.details };
    });
    addItems(newCartItems);
    setLines([]);
    setAddedToCart(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] px-4 py-10">
      <div className="max-w-4xl mx-auto">

        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-serif font-bold text-stone-900">Annað</h1>
            <p className="text-stone-500 font-serif text-sm mt-1">Ólífubrauð, snittubrauð og mini-donuts í pöntun.</p>
          </div>
          {cartCount > 0 && (
            <a href="/karfa" className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl border border-[#C29979] text-[#C29979] font-serif text-sm hover:bg-[#C29979]/5 transition-colors">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 7V6a3 3 0 016 0v1M3 7h18l-2 13H5L3 7z"/></svg>
              <span className="bg-[#C29979] text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{cartCount}</span> Karfa
            </a>
          )}
        </div>

        {/* Product images */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { src: olifubraudImg, label: "Ólífubrauð", price: BREAD_PRICES["Ólífubrauð"] },
            { src: snittubraudImg, label: "Snittubrauð", price: BREAD_PRICES["Snittubrauð"] },
            { src: donutsImg, label: "Mini-donuts", price: MINIDONUT_PRICE_EACH },
          ].map(({ src, label, price }) => (
            <div key={label} className="bg-white rounded-2xl border border-amber-100 shadow overflow-hidden">
              <div className="aspect-[4/3]">
                <img src={src} alt={label} className="w-full h-full object-cover" />
              </div>
              <div className="px-4 py-3">
                <p className="text-stone-800 font-serif font-semibold text-sm">{label}</p>
                <p className="text-stone-400 font-serif text-xs mt-0.5">{formatISK(price)} kr. {label === "Mini-donuts" ? "á stk." : ""}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 min-w-0 space-y-5">

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 font-serif text-sm">{error}</div>
            )}

            {/* Brauð */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 shadow-sm p-6 space-y-4">
              <div>
                <h2 className="text-stone-900 font-serif font-semibold">Brauð</h2>
                <p className="text-stone-400 font-serif text-sm mt-0.5">Ólífubrauð og snittubrauð í pöntun</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Brauðtegund</label>
                  <select className={INPUT} value={breadDraft.bread}
                    onChange={e => setBreadDraft(d => ({ ...d, bread: e.target.value }))}>
                    <option value="">Veldu tegund</option>
                    <option value="Ólífubrauð">Ólífubrauð – {formatISK(BREAD_PRICES["Ólífubrauð"])} kr.</option>
                    <option value="Snittubrauð">Snittubrauð – {formatISK(BREAD_PRICES["Snittubrauð"])} kr.</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Magn</label>
                  <select className={INPUT} value={breadDraft.quantity}
                    onChange={e => setBreadDraft(d => ({ ...d, quantity: e.target.value }))}>
                    <option value="">Veldu magn</option>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                      <option key={n} value={String(n)}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="button" onClick={addBreadLine}
                className="h-10 px-5 rounded-xl bg-[#C29979] text-white font-serif uppercase text-sm hover:bg-[#b08060] transition-colors">
                + Bæta við brauði
              </button>
            </div>

            {/* Mini-donuts */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 shadow-sm p-6 space-y-4">
              <div>
                <h2 className="text-stone-900 font-serif font-semibold">Mini-donuts</h2>
                <p className="text-stone-400 font-serif text-sm mt-0.5">{formatISK(MINIDONUT_PRICE_EACH)} kr. á stk. · lágmark 30 stk.</p>
              </div>
              <div className="max-w-xs">
                <label className={LABEL}>Magn</label>
                <input type="number" min="30" className={INPUT} value={miniQty}
                  onChange={e => setMiniQty(e.target.value)} placeholder="30" />
              </div>
              <button type="button" onClick={addMiniLine}
                className="h-10 px-5 rounded-xl bg-[#C29979] text-white font-serif uppercase text-sm hover:bg-[#b08060] transition-colors">
                + Bæta við minidonuts
              </button>
            </div>

            {addedToCart && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center justify-between">
                <span className="text-emerald-700 font-serif text-sm font-medium">Vörur bættar í körfu!</span>
                <a href="/karfa" className="text-[#C29979] font-serif text-sm underline">Fara í körfu →</a>
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" onClick={() => { setLines([]); setError(""); setAddedToCart(false); }}
                className="h-11 px-5 rounded-xl border border-stone-200 text-stone-500 hover:border-stone-300 font-serif uppercase text-sm transition-colors">
                Hreinsa
              </button>
              <button type="button" onClick={handleAddToCart}
                className="flex items-center gap-2 h-11 px-6 rounded-xl bg-[#C29979] text-white font-serif uppercase text-sm hover:bg-[#b08060] transition-colors">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 7V6a3 3 0 016 0v1M3 7h18l-2 13H5L3 7z"/></svg>
                Bæta í körfu
              </button>
            </div>
          </div>

          {/* Order summary */}
          <aside className="w-full lg:w-64 flex-shrink-0 sticky top-6">
            <div className="bg-amber-50 rounded-2xl border border-amber-200 shadow-sm p-5">
              <h2 className="text-stone-900 font-serif font-semibold mb-4">Yfirlit</h2>
              {lines.length === 0 ? (
                <p className="text-stone-400 font-serif text-sm">Engin vara valin ennþá...</p>
              ) : (
                <div className="space-y-2">
                  {lines.map(l => (
                    <div key={l.id} className="bg-amber-50 rounded-xl border border-amber-100 px-3 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-stone-800 font-serif text-sm font-medium">
                            {l.type === "bread" ? l.details.bread : "Mini-donuts"}
                          </div>
                          <div className="text-stone-400 text-xs font-serif mt-0.5">
                            {l.details.quantity} stk. · {formatISK((l.details.price ?? 0) * Number(l.details.quantity))} kr.
                          </div>
                        </div>
                        <button type="button" onClick={() => removeLine(l.id)}
                          className="text-stone-300 hover:text-red-400 text-xs font-serif transition-colors flex-shrink-0">
                          Eyða
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="pt-3 border-t border-amber-100 flex justify-between font-serif font-semibold text-stone-900 text-sm">
                    <span>Samtals</span>
                    <span className="text-[#C29979]">{formatISK(totals.totalISK)} kr.</span>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>

      </div>
    </div>
  );
};

export default Pantanir;
